/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import cx from 'clsx';

import type { StyleableProps } from '~/types';

import { ChevronDownOutlineIcon24x24 } from '~/assets/icons';

function useScrollTopEnd({ threshold = 50, enabled = false } = {}) {
  const [scrollTopEnd, setScrollTopEnd] = useState<'top' | 'between' | 'end'>(
    'top',
  );

  const blocking = useRef(false);
  const prevScrollY = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    prevScrollY.current = window.pageYOffset;

    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset;
      const scrollTop = 0 + threshold;
      const scrollEnd =
        document.documentElement.scrollHeight - threshold - window.innerHeight;

      if (Math.abs(scrollY - prevScrollY.current) >= threshold) {
        const newScrollTopEnd =
          scrollY <= scrollTop
            ? 'top'
            : scrollY >= scrollEnd
              ? 'end'
              : 'between';
        setScrollTopEnd(newScrollTopEnd);

        prevScrollY.current = scrollY > 0 ? scrollY : 0;
      }

      blocking.current = false;
    };

    const onScroll = () => {
      if (!blocking.current) {
        blocking.current = true;
        window.requestAnimationFrame(updateScrollDirection);
      }
    };

    window.addEventListener('scroll', onScroll);

    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollTopEnd, threshold, enabled]);

  return scrollTopEnd;
}

interface ButtonProps {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function Button({
  style,
  className,
  disabled,
  onClick,
  children,
}: ButtonProps & StyleableProps) {
  return (
    <button
      type="button"
      style={style}
      onClick={onClick}
      className={cx(
        'flex-1 border border-solid border-gray-600 bg-gray-000',
        { 'text-gray-600': disabled },
        className,
      )}
    >
      {children}
    </button>
  );
}

export default function ScrollButtons({
  style,
  className,
  enabled = false,
}: {
  enabled?: boolean;
} & StyleableProps) {
  const scroll = useScrollTopEnd({ enabled });

  if (!enabled) {
    return null;
  }

  return (
    <div
      style={{ height: '90vh', ...style }}
      className={cx(
        'fixed top-1/2 right-12 transform -translate-y-1/2 flex flex-col w-[60px] text-gray-1000 z-[100]',
        className,
      )}
    >
      <Button
        className="rounded-t-[8px]"
        disabled={scroll === 'top'}
        onClick={() =>
          window.scrollBy({
            top: -window.innerHeight / 2,
            behavior: 'smooth',
          })
        }
      >
        <ChevronDownOutlineIcon24x24 className="mx-auto my-0 size-24 rotate-180" />
      </Button>
      <Button
        className="rounded-b-[8px]"
        disabled={scroll === 'end'}
        onClick={() =>
          window.scrollBy({
            top: window.innerHeight / 2,
            behavior: 'smooth',
          })
        }
      >
        <ChevronDownOutlineIcon24x24 className="mx-auto my-0 size-24" />
      </Button>
    </div>
  );
}
